import React, {
  createContext,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'

const generateUniqueId = (() => {
  let id = 0
  return () => String(++id)
})()

// shared state for comment modals
export const CommentModalContext = createContext<
  | {
      commentModeEnabled: boolean
      setCommentModeEnabled: React.Dispatch<SetStateAction<boolean>>

      /** modal id and is open */
      openModals: Record<string, boolean>
      setOpenModals: React.Dispatch<SetStateAction<Record<string, boolean>>>
    }
  | undefined
>(undefined)

export const CommentProvider: React.FC<{children: React.ReactNode}> = ({
  children
}) => {
  const [openModals, setOpenModals] = useState<Record<string, boolean>>({})
  const [commentModeEnabled, setCommentModeEnabled] = useState<boolean>(false)

  return (
    <CommentModalContext.Provider
      value={{
        openModals: openModals,
        setOpenModals,
        commentModeEnabled,
        setCommentModeEnabled
      }}
    >
      {children}
    </CommentModalContext.Provider>
  )
}

/** Only allow one comment modal to be open at a time. If a new one is opened close the others */
export const useCommentModalOpen = (defaultState: boolean) => {
  const context = useContext(CommentModalContext)
  if (!context) {
    throw new Error('useModalOpen must be used within a ModalProvider')
  }

  const [id] = useState(generateUniqueId()) // fixed id for this modal

  const {openModals, setOpenModals} = context

  // run once at mount to register the modal
  // and after to unregister
  useEffect(() => {
    setOpenModals(prev => ({...prev, [id]: defaultState}))
    return () =>
      setOpenModals(prev => {
        const newState = {...prev}
        delete newState[id]
        return newState
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isOpen = openModals[id] ?? false

  const setOpen = useCallback(
    (open: boolean) => {
      setOpenModals(
        prev =>
          Object.fromEntries(
            Object.entries(prev).map(([modalId]) => [
              modalId,
              modalId === id ? open : false
            ])
          ) // close all other modals
      )
    },
    [setOpenModals, id]
  )

  return {isOpen, setOpen}
}

export const useCommentMode = () => {
  const context = useContext(CommentModalContext)
  if (!context) {
    throw new Error('useCommentMode must be used within a CommentProvider')
  }

  const {commentModeEnabled, setCommentModeEnabled} = context

  return {commentModeEnabled, setCommentModeEnabled}
}
